import React, { useContext, useState, useMemo, useEffect } from 'react';
import { DataContext } from '../DataContexts/DataContext';
import { PlaylistDataContext } from '../DataContexts/PlaylistDataContext';
import { PlaytreeDataContext } from '../DataContexts/PlaytreeDataContext';
import CoverArtEditor from '../components/CoverArtEditor'
import PlaylistBackgroundEditor from './PlaylistBackgroundEditor';
import './PlaylistManager.css';

const PlaylistManager = ({ openPlaylistRequest = null }) => {
    const { artists, tags } = useContext(DataContext); // Access playlists, artists, and createPlaylist
    const { playlists, createPlaylist, updatePlaylist, deletePlaylist, removePlaylistBackground } = useContext(PlaylistDataContext); // Access playlists, artists, and createPlaylist
    const { playtrees } = useContext(PlaytreeDataContext);
    const [selectedPlaylist, setSelectedPlaylist] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editName, setEditName] = useState('');
    const [editType, setEditType] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedType, setSelectedType] = useState('All');
    const [songSearchQuery, setSongSearchQuery] = useState('');
    const [songSearchPlaylistQuery, setSongSearchPlaylistQuery] = useState('');
    const [selectedSongs, setSelectedSongs] = useState([]); // Tracks selected with the "+" button
    const [page, setPage] = useState(1);
    const [songsMarkedForRemoval, setSongsMarkedForRemoval] = useState([]);
    const [playlistSortKey, setPlaylistSortKey] = useState('artist'); // Default sort by artist
    const [playlistSortOrder, setPlaylistSortOrder] = useState('asc'); // Default ascending order
    const [availableSortKey, setAvailableSortKey] = useState('artist'); // Default sort by artist
    const [availableSortOrder, setAvailableSortOrder] = useState('asc'); // Default ascending order

    const ITEMS_PER_PAGE = 50;

    useEffect(() => {
        setSongSearchPlaylistQuery(''); // Reset the search box in the middle
        setSongSearchQuery(''); // Reset the search box on the right
    }, [selectedPlaylist]);

    // Handle URL parameters for direct navigation
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const playlistId = urlParams.get('playlistId');
        
        if (playlistId && playlists && playlists.length > 0) {
            const foundPlaylist = playlists.find(playlist => playlist.id === playlistId);
            if (foundPlaylist) {
                setSelectedPlaylist(foundPlaylist);
                setPage(1);
                setSelectedSongs([]);
                setIsEditing(false);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playlists]);

    // Open-playlist requests from the Category Editor (DJB-65): each click
    // produces a fresh request object, so re-opening the same playlist works
    useEffect(() => {
        if (openPlaylistRequest?.id && playlists && playlists.length > 0) {
            const foundPlaylist = playlists.find((playlist) => playlist.id === openPlaylistRequest.id);
            if (foundPlaylist) {
                setSelectedPlaylist(foundPlaylist);
                setPage(1);
                setSelectedSongs([]);
                setIsEditing(false);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openPlaylistRequest]);

    // State for creating a playlist
    const [newPlaylistName, setNewPlaylistName] = useState('');
    const [newPlaylistType, setNewPlaylistType] = useState('');

    // Combine all songs from artists (exclude deleted tracks and tracks from deleted artists)
    const allSongs = useMemo(() => {
        return artists
            .filter((artist) => !artist.deleted)
            .flatMap((artist) =>
                artist.albums.flatMap((album) =>
                    album.tracks
                        .filter((track) => !track.deleted)
                        .map((track) => ({
                            ...track,
                            artistName: artist.name,
                            artistId: artist.id,
                        }))
                )
            );
    }, [artists]);

    // Filter playlists based on search query and selected type
    const filteredPlaylists = useMemo(() => {
        if (!playlists) return [];
        return playlists
            .filter(
                (playlist) => {
                    const searchLower = searchQuery.toLowerCase();
                    const searchStringMatch = playlist.searchString?.toLowerCase().includes(searchLower) || false;
                    const nameMatch = playlist.name?.toLowerCase().includes(searchLower) || false;
                    return (searchStringMatch || nameMatch) &&
                        (selectedType === 'All' || playlist.playlistType === selectedType);
                }
            )
            .sort((a, b) => (a.searchString || a.name || '').localeCompare(b.searchString || b.name || '')); // Sort alphabetically by searchString
    }, [playlists, searchQuery, selectedType]);

    // Shared matcher for the available-songs search: title, artist, genre
    // and tag names (DJB-64)
    const matchesSongSearch = useMemo(() => {
        const q = songSearchQuery.toLowerCase().trim();
        return (song) =>
            song.title.toLowerCase().includes(q) ||
            song.artistName.toLowerCase().includes(q) ||
            (song.genre || '').toLowerCase().includes(q) ||
            (song.tags && tags
                .filter(tag => song.tags.includes(tag.id))
                .some(tag => tag.name.toLowerCase().includes(q)));
    }, [songSearchQuery, tags]);

    const filteredSongs = useMemo(() => {
        if (!selectedPlaylist) return [];

        const filtered = allSongs.filter(
            (song) => !selectedPlaylist.tracks.includes(song.id) && matchesSongSearch(song)
        );

        // Deduplicate by id so the same track (e.g. in multiple albums) appears once
        const byId = new Map();
        filtered.forEach((s) => { if (!byId.has(s.id)) byId.set(s.id, s); });
        const unique = Array.from(byId.values());

        return unique
            .sort((a, b) => {
                let comparison = 0;
                if (availableSortKey === 'artist') {
                    comparison = a.artistName.toLowerCase().localeCompare(b.artistName.toLowerCase());
                    if (comparison === 0) comparison = a.title.toLowerCase().localeCompare(b.title.toLowerCase());
                } else if (availableSortKey === 'title') {
                    comparison = a.title.toLowerCase().localeCompare(b.title.toLowerCase());
                    if (comparison === 0) comparison = a.artistName.toLowerCase().localeCompare(b.artistName.toLowerCase());
                } else if (availableSortKey === 'genre') {
                    comparison = (a.genre || '').toLowerCase().localeCompare((b.genre || '').toLowerCase());
                    if (comparison === 0) comparison = a.title.toLowerCase().localeCompare(b.title.toLowerCase());
                }
                return availableSortOrder === 'asc' ? comparison : -comparison;
            })
            .slice(0, page * ITEMS_PER_PAGE);
    }, [allSongs, selectedPlaylist, matchesSongSearch, page, availableSortKey, availableSortOrder]);




    const filteredPlaylistSongs = useMemo(() => {
        if (!selectedPlaylist) return [];

        const songs = selectedPlaylist.tracks
            .map((trackId) => allSongs.find((song) => song.id === trackId))
            .filter((track) =>
                track && (
                    track.title.toLowerCase().includes(songSearchPlaylistQuery.toLowerCase()) ||
                    track.artistName.toLowerCase().includes(songSearchPlaylistQuery.toLowerCase())
                )
            );

        return songs.sort((a, b) => {
            let comparison = 0;

            if (playlistSortKey === 'artist') {
                // Sort by artist first, then by title
                comparison = a.artistName.toLowerCase().localeCompare(b.artistName.toLowerCase());
                if (comparison === 0) {
                    comparison = a.title.toLowerCase().localeCompare(b.title.toLowerCase());
                }
            } else if (playlistSortKey === 'title') {
                // Sort by title first, then by artist
                comparison = a.title.toLowerCase().localeCompare(b.title.toLowerCase());
                if (comparison === 0) {
                    comparison = a.artistName.toLowerCase().localeCompare(b.artistName.toLowerCase());
                }
            }

            return playlistSortOrder === 'asc' ? comparison : -comparison;
        });
    }, [selectedPlaylist, allSongs, songSearchPlaylistQuery, playlistSortKey, playlistSortOrder]);



    const togglePlaylistSort = (key) => {
        if (playlistSortKey === key) {
            // Toggle sorting order (asc <-> desc)
            setPlaylistSortOrder(playlistSortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            // Change sorting key and reset order to ascending
            setPlaylistSortKey(key);
            setPlaylistSortOrder('asc');
        }
    };

    const toggleAvailableSort = (key) => {
        if (availableSortKey === key) {
            setAvailableSortOrder(availableSortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setAvailableSortKey(key);
            setAvailableSortOrder('asc');
        }
    };



    // Get CUSTOM_ playlist types for the dropdown
    const customPlaylistTypes = useMemo(() => {
        if (!playlists) return [];
        return [...new Set(playlists.map((playlist) => playlist.playlistType))].filter((type) =>
            type.startsWith('CUSTOM_')
        );
    }, [playlists]);

    // Create a new playlist
    const handleCreatePlaylist = async () => {
        if (!newPlaylistName.trim() || !newPlaylistType) {
            alert('Please enter a name and select a playlist type.');
            return;
        }

        const newPlaylist = {
            name: newPlaylistName.trim(),
            playlistType: newPlaylistType,
            tracks: [],
        };

        try {
            await createPlaylist(newPlaylist); // Call createPlaylist from DataContext
            setNewPlaylistName(''); // Clear name
            setNewPlaylistType(''); // Clear type
        } catch (error) {
        }
    };

    // Enter edit mode
    const handleEditClick = () => {
        setIsEditing(true);
        setEditName(selectedPlaylist.name);
        setEditType(selectedPlaylist.playlistType);
        setEditDescription(selectedPlaylist.description || '');
    };

    const handleSaveClick = async () => {
        // For GENERIC_GLOBAL playlists, don't modify tracks
        const updatedTracks = selectedPlaylist.playlistType === 'GENERIC_GLOBAL' 
            ? selectedPlaylist.tracks 
            : selectedPlaylist.tracks.filter(
                (trackId) => !songsMarkedForRemoval.includes(trackId)
            );

        const updatedPlaylist = {
            ...selectedPlaylist,
            name: editName,
            playlistType: selectedPlaylist.playlistType === 'GENERIC_GLOBAL' ? selectedPlaylist.playlistType : editType,
            description: editDescription,
            tracks: updatedTracks,
        };

        try {
            await updatePlaylist(updatedPlaylist); // Update playlist in DataContext
            setSelectedPlaylist(updatedPlaylist); // Update locally
            setSongsMarkedForRemoval([]); // Clear marked songs
            setIsEditing(false);
        } catch (error) {
            console.error('Failed to save playlist:', error);
        }
    };


    // Cancel editing
    const handleCancelClick = () => {
        setIsEditing(false);
        setEditName(selectedPlaylist.name); // Reset name to the original
        setEditType(selectedPlaylist.playlistType); // Reset type to the original
        setEditDescription(selectedPlaylist.description || ''); // Reset description to the original
        setSongsMarkedForRemoval([]); // Clear marked songs
    };


    // Add a song to the intermediate table
    const handleAddSongToIntermediate = (song) => {
        if (!selectedSongs.find((selected) => selected.id === song.id)) {
            setSelectedSongs([...selectedSongs, song]);
        }
    };

    const handleRemoveFromPlaylist = (trackId) => {
        if (songsMarkedForRemoval.includes(trackId)) {
            // Unmark the song for removal
            setSongsMarkedForRemoval(songsMarkedForRemoval.filter((id) => id !== trackId));
        } else {
            // Mark the song for removal
            setSongsMarkedForRemoval([...songsMarkedForRemoval, trackId]);
        }
    };

    const handleAddAllToIntermediate = () => {
        const newSongs = filteredSongs.filter(
            (song) => !selectedSongs.some((selected) => selected.id === song.id)
        );
        setSelectedSongs([...selectedSongs, ...newSongs]);
    };

    // Remove a song from the intermediate table
    const handleRemoveFromIntermediate = (songId) => {
        setSelectedSongs((prevSongs) =>
            prevSongs.filter((song) => song.id !== songId)
        );
    };

    // Add selected songs to the playlist
    const handleAddToPlaylist = async () => {
        if (selectedPlaylist) {
            const updatedPlaylist = {
                ...selectedPlaylist,
                tracks: [...selectedPlaylist.tracks, ...selectedSongs.map((song) => song.id)],
            };
            try {
                await updatePlaylist(updatedPlaylist); // Update playlist in DataContext
                setSelectedPlaylist(updatedPlaylist); // Update locally
                setSelectedSongs([]); // Clear the intermediate table
            } catch (error) {
                console.error('Failed to add songs to playlist:', error);
            }
        }
    };

    const filteredSongsTotal = useMemo(() => {
        if (!selectedPlaylist) return 0;
        const filtered = allSongs.filter(
            (song) => !selectedPlaylist.tracks.includes(song.id) && matchesSongSearch(song)
        );
        const byId = new Map();
        filtered.forEach((s) => byId.set(s.id, s));
        return byId.size;
    }, [allSongs, selectedPlaylist, matchesSongSearch]);

    // Load the next page slightly before the bottom is reached, like the
    // Blocked Content catalogue, so scrolling never hits a hard stop
    const handleScroll = (e) => {
        const el = e.target;
        if (
            el.scrollTop + el.clientHeight >= el.scrollHeight - 10 &&
            filteredSongs.length < filteredSongsTotal
        ) {
            setPage((prevPage) => prevPage + 1);
        }
    };


    // Playlist background (DJB-235): one plain-uploaded image per playlist,
    // stored as PLAYLIST.backgroundImage and applied on every machine that
    // plays the playlist. Choosing a new one replaces the previous
    // background. The other pending edits (name/description) are not
    // touched: the current stored playlist is sent with only the background
    // changed. Resolves with the file name the server kept, undefined on failure.
    //
    // The upload endpoint registers the background asynchronously; the first
    // PUT can land before that and be refused (server keeps the old value).
    const handleBackgroundSelect = async (fileName) => {
        if (!selectedPlaylist || !fileName) return undefined;
        const next = { ...selectedPlaylist, backgroundImage: fileName };
        let effective = null;
        for (let attempt = 0; attempt < 3; attempt += 1) {
            if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, 700));
            const saved = await updatePlaylist(next);
            if (saved === null) return undefined; // request failed, notification shown by the context
            effective = saved ? { ...next, ...saved } : next;
            if (effective.backgroundImage === fileName) break;
        }
        setSelectedPlaylist(effective);
        return effective.backgroundImage ?? null;
    };

    const handleBackgroundRemove = async () => {
        if (!selectedPlaylist?.backgroundImage) return;
        const removed = await removePlaylistBackground(selectedPlaylist.id);
        if (removed) {
            setSelectedPlaylist((prev) => (prev ? { ...prev, backgroundImage: null } : prev));
        }
    };

    const handleDeletePlayList = async () => {
        if (selectedPlaylist) {
            const confirmDelete = window.confirm(
                `Are you sure you want to delete the playlist "${selectedPlaylist.name}"?`
            );
            if (confirmDelete) {
                await deletePlaylist(selectedPlaylist.id);
                setSelectedPlaylist(null); // Deselect playtree after deletion
            }
        }
    };

    return (
        <div className="playlist-manager">
            {/* Create Playlist – centered wrap at the top of the section */}
            <div className="pm-create-wrap">
                <div className="create-playlist-container">
                    <span className="create-playlist-label">Create new playlist:</span>
                    <label>
                        Name:
                        <input
                            type="text"
                            className="create-playlist-name"
                            value={newPlaylistName}
                            onChange={(e) => setNewPlaylistName(e.target.value)}
                        />
                    </label>
                    <label>
                        Type:
                        <select
                            className="create-playlist-type"
                            value={newPlaylistType}
                            onChange={(e) => setNewPlaylistType(e.target.value)}
                        >
                            <option value="">Select a type</option>
                            {customPlaylistTypes.map((type) => (
                                <option key={type} value={type}>
                                    {type}
                                </option>
                            ))}
                        </select>
                    </label>
                    <button className="create-playlist-button" onClick={handleCreatePlaylist}>
                        Create Playlist
                    </button>
                </div>
            </div>

            {/* The legacy `columns`/`column` classes stay for the standalone
                /playlistmanager route; the pm-* classes carry the modern grid
                inside the Curator page (DJB-70). The editing modifier lets the
                grid widen the details column while a playlist is being edited
                without anything clipping (DJB-71) */}
            <div className={`columns pm-columns${isEditing ? ' pm-columns--editing' : ''}`}>
                {/* Left column - type filter, search and playlist list, modelled
                    on the Playlist Subscriptions left column */}
                <div className="column pm-column pm-column--list">
                    <div className="ps-column-head">
                        <span className="ps-column-label">Playlists</span>
                        <span className="ps-column-count">{filteredPlaylists.length}</span>
                    </div>
                    <div className="ps-filters">
                        <select
                            className="ps-select"
                            aria-label="Filter by playlist type"
                            value={selectedType}
                            onChange={(e) => setSelectedType(e.target.value)}
                        >
                            {['All', ...new Set(playlists.map((playlist) => playlist.playlistType))].map(
                                (type) => (
                                    <option key={type} value={type}>
                                        {type}
                                    </option>
                                )
                            )}
                        </select>
                        <input
                            type="text"
                            placeholder="Search playlists..."
                            className="ps-input"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <ul className="playlist-list-content">
                        {filteredPlaylists.map((playlist) => (
                            <li
                                key={playlist.id}
                                className={
                                    selectedPlaylist?.id === playlist.id ? 'selected' : ''
                                }
                                onClick={() => {
                                    setSelectedPlaylist(playlist);
                                    setPage(1); // Reset the page for third column scrolling
                                    setSelectedSongs([]); // Clear intermediate table
                                    setIsEditing(false); // Exit edit mode if switching playlists
                                }}
                            >
                                <span className="playlist-name">{playlist.name === 'YOUR FAVOURITES' ? (playlist.searchString || playlist.name) : playlist.name}</span>
                                <span className="playlist-track-count">
                                    {playlist.tracks.length} {playlist.tracks.length === 1 ? 'track' : 'tracks'}
                                </span>

                            </li>
                        ))}
                    </ul>
                </div>

                {/* Middle column - Playlist details */}
                <div className="column pm-column pm-column--details">
                    {selectedPlaylist ? (
                        <div className="playlist-details">
                            {/* Playlist Header (DJB-235, compact): the art
                                block (cover art beside the name-block, the meta
                                in one wrapping line under it) with the
                                Edit/Delete buttons pinned top-right, and the
                                background strip on its own row below (DJB-75
                                keeps the buttons a direct header child) */}
                            <div className="playlist-header pm-header">
                                <div className="pm-art-block">
                                    <CoverArtEditor
                                        resourceId={selectedPlaylist.id} template="playlist"
                                    />
                                    <div className="playlist-info">
                                        {isEditing ? (
                                            <div className="pm-edit-fields">
                                                <input
                                                    type="text"
                                                    value={editName}
                                                    onChange={(e) => setEditName(e.target.value)}
                                                    className="edit-input pm-edit-name"
                                                    aria-label="Playlist name"
                                                />
                                                {selectedPlaylist.playlistType !== 'GENERIC_GLOBAL' && (
                                                    <select
                                                        value={editType}
                                                        onChange={(e) => setEditType(e.target.value)}
                                                        className="edit-input pm-edit-type"
                                                        aria-label="Playlist type"
                                                    >
                                                        {customPlaylistTypes.map((type) => (
                                                            <option key={type} value={type}>
                                                                {type}
                                                            </option>
                                                        ))}
                                                    </select>
                                                )}
                                                <textarea
                                                    value={editDescription}
                                                    onChange={(e) => setEditDescription(e.target.value)}
                                                    className="edit-input pm-edit-description"
                                                    placeholder="Description"
                                                    aria-label="Description"
                                                    rows={2}
                                                />
                                            </div>
                                        ) : (
                                            <h4>{selectedPlaylist.name === 'YOUR FAVOURITES' ? (selectedPlaylist.searchString || selectedPlaylist.name) : selectedPlaylist.name}</h4>
                                        )}
                                        <p className="pm-song-count">
                                            Songs: {selectedPlaylist.tracks.length}
                                        </p>
                                    </div>
                                    {!isEditing && (
                                        <div className="pm-art-meta">
                                            <p>Type: {selectedPlaylist.playlistType}</p>
                                            <p>
                                                Categories:{' '}
                                                {playtrees
                                                    .filter((playtree) =>
                                                        playtree.sublists.includes(selectedPlaylist.id)
                                                    )
                                                    .map((playtree) => playtree.name)
                                                    .join(', ') || 'None'}
                                            </p>
                                            <p>
                                                ID: {selectedPlaylist.id}
                                            </p>
                                            <p
                                                className="pm-art-meta-description"
                                                title={selectedPlaylist.description || undefined}
                                            >
                                                Description: {selectedPlaylist.description || 'N/A'}
                                            </p>
                                        </div>
                                    )}
                                </div>
                                {((selectedPlaylist.playlistType.startsWith('CUSTOM_') && selectedPlaylist.name !== 'DYNAMIC-LOCAL') || selectedPlaylist.playlistType === 'GENERIC_GLOBAL') && (
                                    <div className="edit-buttonsPlaytrees">
                                        {isEditing ? (
                                            <>
                                                <button
                                                    className="edit-buttonPlaytrees"
                                                    onClick={handleSaveClick}
                                                >
                                                    Save
                                                </button>
                                                <button
                                                    className="cancel-buttonPlaytrees"
                                                    onClick={handleCancelClick}
                                                >
                                                    Cancel
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    className="edit-buttonPlaytrees"
                                                    onClick={handleEditClick}
                                                >
                                                    Edit
                                                </button>
                                                {selectedPlaylist.playlistType.startsWith('CUSTOM_') && selectedPlaylist.name !== 'STATIC-LOCAL' && (
                                                    <button
                                                        className="delete-buttonPlaytrees"
                                                        onClick={handleDeletePlayList}
                                                    >
                                                        Delete
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                                <PlaylistBackgroundEditor
                                    playlistId={selectedPlaylist.id}
                                    fileName={selectedPlaylist.backgroundImage || null}
                                    onSelect={handleBackgroundSelect}
                                    onRemove={handleBackgroundRemove}
                                />
                            </div>

                            {/* Songs Table – the search bar lives in the sticky
                                table header instead of a separate box (DJB-71) */}
                            <table className="playlist-table">
                                <thead>
                                    <tr className="pm-table-search-row">
                                        <th
                                            className="pm-table-search-th"
                                            colSpan={isEditing && selectedPlaylist.playlistType !== 'GENERIC_GLOBAL' ? 5 : 3}
                                        >
                                            <input
                                                type="text"
                                                placeholder="Search in playlist..."
                                                className="search-box pm-table-search"
                                                value={songSearchPlaylistQuery}
                                                onChange={(e) => setSongSearchPlaylistQuery(e.target.value)}
                                            />
                                        </th>
                                    </tr>
                                    <tr>
                                        <th onClick={() => togglePlaylistSort('title')}>
                                            Title {playlistSortKey === 'title' ? (playlistSortOrder === 'asc' ? '↑' : '↓') : ''}
                                        </th>
                                        <th onClick={() => togglePlaylistSort('artist')}>
                                            Artist {playlistSortKey === 'artist' ? (playlistSortOrder === 'asc' ? '↑' : '↓') : ''}
                                        </th>
                                        <th className="playlist-track-flags-col"></th>
                                        {isEditing && selectedPlaylist.playlistType !== 'GENERIC_GLOBAL' && <th></th>}
                                        {isEditing && selectedPlaylist.playlistType !== 'GENERIC_GLOBAL' && <th></th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredPlaylistSongs.map((track) => (
                                        <tr
                                            key={track.id}
                                            style={{
                                                backgroundColor: songsMarkedForRemoval.includes(track.id) ? '#ffcccc' : 'transparent',
                                            }}
                                        >
                                            <td>
                                                <a 
                                                    href={`/music-manager?artistId=${track.artistId}&songId=${track.id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="song-link"
                                                >
                                                    {track.title}
                                                </a>
                                            </td>
                                            <td>{track.artistName}</td>
                                            <td className="playlist-track-flags">
                                                {(track.neverIdle || track.onDemand || track.ondemand) && (
                                                    <>
                                                        {track.neverIdle && <span className="song-flag song-flag-ni">NI</span>}
                                                        {(track.onDemand || track.ondemand) && <span className="song-flag song-flag-od">OD</span>}
                                                    </>
                                                )}
                                            </td>
                                            {isEditing && selectedPlaylist.playlistType !== 'GENERIC_GLOBAL' && (
                                                <td>
                                                    <button
                                                        className={songsMarkedForRemoval.includes(track.id) ? 'add-button' : 'remove-button'}
                                                        onClick={() => handleRemoveFromPlaylist(track.id)}
                                                    >
                                                        {songsMarkedForRemoval.includes(track.id) ? '+' : '-'}
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p>Select a playlist to view details.</p>
                    )}
                </div>



                {/* Right column - Song search and list */}
                <div className="column pm-column pm-column--work">
                    {!selectedPlaylist ? (
                        <p>Select a playlist to add songs.</p>
                    ) : selectedPlaylist.playlistType.startsWith('GENERIC') ? (
                        <p>Cannot add songs to GENERIC playlists.</p>
                    ) : (
                        <>
                            {/* Intermediate Table */}
                            {selectedSongs.length > 0 && (
                                <div className="intermediate-table">
                                    <h5>Selected Songs</h5>
                                    <table className="playlist-table">
                                        <thead>
                                            <tr>
                                                <th>Title</th>
                                                <th>Artist</th>
                                                <th className="playlist-track-flags-col"></th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedSongs.map((song) => (
                                                <tr key={song.id}>
                                                    <td>
                                                        <a 
                                                            href={`/music-manager?artistId=${song.artistId}&songId=${song.id}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="song-link"
                                                        >
                                                            {song.title}
                                                        </a>
                                                    </td>
                                                    <td>{song.artistName}</td>
                                                    <td className="playlist-track-flags">
                                                        {(song.neverIdle || song.onDemand || song.ondemand) && (
                                                            <>
                                                                {song.neverIdle && <span className="song-flag song-flag-ni">NI</span>}
                                                                {(song.onDemand || song.ondemand) && <span className="song-flag song-flag-od">OD</span>}
                                                            </>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <button
                                                            className="remove-button"
                                                            onClick={() => handleRemoveFromIntermediate(song.id)}
                                                        >
                                                            X
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    <div className="intermediate-actions">
                                        <button className="edit-button" onClick={handleAddToPlaylist}>
                                            Add to Current Playlist
                                        </button>
                                        <button
                                            className="cancel-button"
                                            onClick={() => setSelectedSongs([])} // Clears the intermediate table
                                        >
                                            Remove All
                                        </button>
                                    </div>
                                </div>
                            )}


                            {/* Song List */}
                            <input
                                type="text"
                                placeholder="Search by title, artist, genre or tag..."
                                className="search-box"
                                value={songSearchQuery}
                                onChange={(e) => {
                                    setSongSearchQuery(e.target.value);
                                    setPage(1);
                                }}
                            />
                            <div className="pm-songs-scroll" onScroll={handleScroll}>
                            <table className="playlist-table" key={`available-songs-${songSearchQuery}`}>
                                <thead>
                                    <tr>
                                        <th onClick={() => toggleAvailableSort('title')}>
                                            Title {availableSortKey === 'title' ? (availableSortOrder === 'asc' ? '↑' : '↓') : ''}
                                        </th>
                                        <th onClick={() => toggleAvailableSort('artist')}>
                                            Artist {availableSortKey === 'artist' ? (availableSortOrder === 'asc' ? '↑' : '↓') : ''}
                                        </th>
                                        <th onClick={() => toggleAvailableSort('genre')}>
                                            Genre {availableSortKey === 'genre' ? (availableSortOrder === 'asc' ? '↑' : '↓') : ''}
                                        </th>
                                        <th className="playlist-track-flags-col"></th>
                                        <th>
                                            <button
                                                className="add-button"
                                                onClick={() => handleAddAllToIntermediate()}
                                            >
                                                Add All
                                            </button>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredSongs.map((song) => (
                                        <tr key={song.id}>
                                            <td>
                                                <a
                                                    href={`/music-manager?artistId=${song.artistId}&songId=${song.id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="song-link"
                                                >
                                                    {song.title}
                                                </a>
                                            </td>
                                            <td>{song.artistName}</td>
                                            <td>{song.genre || ''}</td>
                                            <td className="playlist-track-flags">
                                                {(song.neverIdle || song.onDemand || song.ondemand) && (
                                                    <>
                                                        {song.neverIdle && <span className="song-flag song-flag-ni">NI</span>}
                                                        {(song.onDemand || song.ondemand) && <span className="song-flag song-flag-od">OD</span>}
                                                    </>
                                                )}
                                            </td>
                                            <td>
                                                <button
                                                    className="add-button"
                                                    onClick={() => handleAddSongToIntermediate(song)}
                                                >
                                                    +
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {filteredSongs.length < filteredSongsTotal && (
                                        <tr>
                                            <td colSpan={5} className="pm-loading-cell">Loading more...</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                            </div>
                        </>
                    )}
                </div>

            </div>
        </div>
    );
};

export default PlaylistManager;
